import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type DateRange,
  SearchAndSort,
  SortOption,
  Pagination,
  EmptyState,
  SkeletonTable,
  useRankingTabOptions,
  TrendIndicator,
  PositionBadge,
} from "../../features/statsNew/components";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import {
  useRankingData,
  useRankingTrends,
  type RankingEntityType,
  type RankingSortKey,
  type RankingItem,
} from "../../features/statsNew/hooks";
import {
  getAllSessionsByProjectGroup,
  sessionsByProjectGroupSelector,
} from "../../features/stats/state/statsSlice";
import { AppDispatch } from "../../common/state/store";
import { setDateRange } from "../../features/statsNew/state/statsNewSlice";

interface OutletContextType {
  dateRange: DateRange;
}

// Medal emojis for top 3
const getMedalEmoji = (rank: number): string => {
  switch (rank) {
    case 1:
      return "🥇";
    case 2:
      return "🥈";
    case 3:
      return "🥉";
    default:
      return "";
  }
};

const ClassementView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { dateRange } = useOutletContext<OutletContextType>();
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  const [entityType, setEntityType] = useState<RankingEntityType>("parcours");
  const rankingTabOptions = useRankingTabOptions();
  const [sortKey, setSortKey] = useState<RankingSortKey>("totalSessions");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Fetch data on mount
  useEffect(() => {
    dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewClassementView" }));
  }, [dispatch]);

  // Track when data has loaded
  useEffect(() => {
    if (sessionsByProjectGroup.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [sessionsByProjectGroup, isInitialLoad]);

  // Get ranking data
  const { data: rankingData, totalCount } = useRankingData({
    dateRange,
    entityType,
    sortKey,
    sortDirection,
    searchValue,
  });

  // Get trends
  const { trends, hasTrends, previousPeriodLabel } = useRankingTrends({
    dateRange,
    entityType,
    sortKey,
  });

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [entityType, sortKey, sortDirection, searchValue, dateRange]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return rankingData.slice(start, start + pageSize);
  }, [rankingData, currentPage, pageSize]);

  // Sort options for the dropdown
  const sortOptions: SortOption[] = [
    { key: "totalSessions", label: t("statsNew.classement.columns.totalSessions") },
    { key: "completedSessions", label: t("statsNew.classement.columns.completedSessions") },
    { key: "completionRate", label: t("statsNew.classement.columns.completionRate") },
    { key: "averageScore", label: t("statsNew.classement.columns.averageScore") },
  ];

  const handleSortChange = (key: string) => {
    if (key === sortKey) {
      // Toggle direction
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key as RankingSortKey);
      setSortDirection("desc");
    }
  };

  const handleColumnSort = (key: RankingSortKey) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  const handleRowClick = (item: RankingItem) => {
    if (entityType === "parcours") {
      navigate(`/statistics/parcours/${item.id}`);
    } else if (entityType === "modules") {
      // Find the parcours that contains this module
      const group = sessionsByProjectGroup.find((g) =>
        g.projects.some((p) => p.project_id === item.id),
      );
      if (group) {
        navigate(`/statistics/parcours/${group.project_group_id}/${item.id}`);
      }
    } else if (entityType === "apprenants") {
      // Extract user ID from the composite ID
      const userId = String(item.id).replace(/^(user-|name-)/, "");
      if (String(item.id).startsWith("user-")) {
        navigate(`/statistics/apprenants/${userId}`);
      }
      // For name-based users, we can't navigate as they don't have a user profile
    }
  };

  const handleClearDateRange = () => {
    dispatch(setDateRange({ startDate: null, endDate: null }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  // Render sort indicator
  const SortIndicator = ({ column }: { column: RankingSortKey }) => {
    if (column !== sortKey) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return sortDirection === "desc" ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-green-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-green-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );
  };

  // Show skeleton during initial load
  if (isInitialLoad) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-4">
          <div className="h-10 w-64 bg-gray-200 rounded animate-pulse mb-4" />
          <div className="h-10 w-full max-w-md bg-gray-200 rounded animate-pulse" />
        </div>
        <SkeletonTable rows={10} columns={7} />
      </div>
    );
  }

  // Show empty state if no data
  if (totalCount === 0) {
    if (searchValue) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 mb-4 space-y-4">
            <SegmentedControl<RankingEntityType> options={rankingTabOptions} value={entityType} onChange={setEntityType} size="lg" />
            <SearchAndSort
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              searchPlaceholder={t("statsNew.classement.searchPlaceholder")}
              sortOptions={sortOptions}
              selectedSort={sortKey}
              onSortChange={handleSortChange}
            />
          </div>
          <EmptyState
            icon="search"
            title={t("statsNew.classement.noSearchResults")}
            description={t("statsNew.emptyState.noSearchResultsDescription")}
          />
        </div>
      );
    }

    if (dateRange.startDate || dateRange.endDate) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 mb-4 space-y-4">
            <SegmentedControl<RankingEntityType> options={rankingTabOptions} value={entityType} onChange={setEntityType} size="lg" />
          </div>
          <EmptyState
            icon="calendar"
            title={t("statsNew.classement.emptyState.noData")}
            description={t("statsNew.classement.emptyState.noDataDescription")}
            action={{
              label: t("statsNew.emptyState.viewAll"),
              onClick: handleClearDateRange,
            }}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-4 space-y-4">
          <SegmentedControl<RankingEntityType> options={rankingTabOptions} value={entityType} onChange={setEntityType} size="lg" />
        </div>
        <EmptyState
          icon="chart"
          title={t("statsNew.classement.emptyState.noData")}
          description={t("statsNew.emptyState.noDataDescription")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tabs and controls */}
      <div className="flex-shrink-0 mb-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <SegmentedControl<RankingEntityType> options={rankingTabOptions} value={entityType} onChange={setEntityType} size="lg" />
          {hasTrends && previousPeriodLabel && (
            <span className="text-sm text-gray-500">{previousPeriodLabel}</span>
          )}
        </div>
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.classement.searchPlaceholder")}
          sortOptions={sortOptions}
          selectedSort={sortKey}
          onSortChange={handleSortChange}
        />
      </div>

      {/* Trends not available notice */}
      {!hasTrends && (
        <div className="flex-shrink-0 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">
            {t("statsNew.classement.emptyState.noTrendsDescription")}
          </p>
        </div>
      )}

      {/* Ranking table */}
      <div className="flex-1 overflow-auto min-h-0">
        <table
          className="stats-table"
          role="table"
          aria-label={t("statsNew.classement.tableLabel")}
        >
          <thead>
            <tr role="row">
              <th className="w-16" scope="col">
                {t("statsNew.classement.columns.rank")}
              </th>
              <th scope="col">{t("statsNew.classement.columns.name")}</th>
              <th
                className="sortable text-center"
                onClick={() => handleColumnSort("totalSessions")}
                scope="col"
                aria-sort={
                  sortKey === "totalSessions"
                    ? sortDirection === "desc"
                      ? "descending"
                      : "ascending"
                    : "none"
                }
              >
                <div className="flex items-center justify-center gap-1">
                  {t("statsNew.classement.columns.totalSessions")}
                  <SortIndicator column="totalSessions" />
                </div>
              </th>
              <th
                className="sortable text-center"
                onClick={() => handleColumnSort("completedSessions")}
                scope="col"
                aria-sort={
                  sortKey === "completedSessions"
                    ? sortDirection === "desc"
                      ? "descending"
                      : "ascending"
                    : "none"
                }
              >
                <div className="flex items-center justify-center gap-1">
                  {t("statsNew.classement.columns.completedSessions")}
                  <SortIndicator column="completedSessions" />
                </div>
              </th>
              <th
                className="sortable text-center"
                onClick={() => handleColumnSort("completionRate")}
                scope="col"
                aria-sort={
                  sortKey === "completionRate"
                    ? sortDirection === "desc"
                      ? "descending"
                      : "ascending"
                    : "none"
                }
              >
                <div className="flex items-center justify-center gap-1">
                  {t("statsNew.classement.columns.completionRate")}
                  <SortIndicator column="completionRate" />
                </div>
              </th>
              <th
                className="sortable text-center"
                onClick={() => handleColumnSort("averageScore")}
                scope="col"
                aria-sort={
                  sortKey === "averageScore"
                    ? sortDirection === "desc"
                      ? "descending"
                      : "ascending"
                    : "none"
                }
              >
                <div className="flex items-center justify-center gap-1">
                  {t("statsNew.classement.columns.averageScore")}
                  <SortIndicator column="averageScore" />
                </div>
              </th>
              {hasTrends && (
                <th className="text-center w-24" scope="col">
                  {t("statsNew.classement.columns.position")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item) => {
              const trend = trends[String(item.id)];
              const medal = getMedalEmoji(item.rank);

              return (
                <tr key={item.id} onClick={() => handleRowClick(item)} className="clickable">
                  {/* Rank */}
                  <td className="whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {medal ? (
                        <span className="text-lg">{medal}</span>
                      ) : (
                        <span className="font-medium text-gray-600">{item.rank}</span>
                      )}
                    </div>
                  </td>

                  {/* Name */}
                  <td>
                    <div className="font-medium text-gray-900 truncate max-w-xs">{item.name}</div>
                  </td>

                  {/* Total Sessions */}
                  <td className="whitespace-nowrap text-center">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-gray-900">{item.totalSessions}</span>
                      {hasTrends && trend?.totalSessions && (
                        <TrendIndicator trend={trend.totalSessions} />
                      )}
                    </div>
                  </td>

                  {/* Completed Sessions */}
                  <td className="whitespace-nowrap text-center">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-gray-900">{item.completedSessions}</span>
                      {hasTrends && trend?.completedSessions && (
                        <TrendIndicator trend={trend.completedSessions} />
                      )}
                    </div>
                  </td>

                  {/* Completion Rate */}
                  <td className="whitespace-nowrap text-center">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-gray-900">{item.completionRate}%</span>
                      {hasTrends && trend?.completionRate && (
                        <TrendIndicator trend={trend.completionRate} />
                      )}
                    </div>
                  </td>

                  {/* Average Score */}
                  <td className="whitespace-nowrap text-center">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-gray-900">
                        {item.averageScore !== null && item.averageScoreMax !== null
                          ? `${item.averageScore}/${item.averageScoreMax} pts`
                          : "-"}
                      </span>
                      {hasTrends && trend?.averageScore && (
                        <TrendIndicator trend={trend.averageScore} />
                      )}
                    </div>
                  </td>

                  {/* Position Change */}
                  {hasTrends && (
                    <td className="whitespace-nowrap text-center">
                      {trend ? (
                        <PositionBadge change={trend.positionChange} />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex-shrink-0 mt-auto">
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
    </div>
  );
};

export default ClassementView;
